import SceneObject from "../../../lib/services/scene/sceneobject";
import ShaderService from "../../../lib/services/shader/shader_service";
import Camera from "../../../lib/services/scene/camera";
import ResourceService from "../../../lib/services/resource/resourceservice";
import CharacterBuilder from "../../../lib/services/render/builders/character_builder";
import { CharacterRuntime } from "../../../lib/services/render/builders/character_runtime";
import { PuppyClips } from "./puppyclips";
import BlobShadow from "../../../lib/services/scene/blob_shadow";
import PuppyNavigator from "./puppynavigator";
import PuppyBrain from "./puppybrain";
import SphereCollisionVolume from "../../../lib/services/collision/sphere_collision_volume";
import Stage from "../../../lib/services/stage/stage";
import PuppySequenceDebugRunner from "./puppy_sequence_debug_runner";

import CharacterBuilderGpu from "../../../lib/services/compute/character_builder_gpu";
import AnimationLibraryPool from "../../../lib/services/compute/animation_library_pool";
import AnimationPool from "../../../lib/services/compute/animation_pool";
import AnimationComputePipeline from "../../../lib/services/compute/pipelines/animation_compute_pipeline";
import AnimationControllerGpu from "../../../lib/services/animation/animation_controller_gpu";
import JointAttachment from "../../../lib/services/animation/joint_attachment";
import { trsToMat4 } from "../../../lib/services/animation/anim_math";
import AttachmentPlacement from "../../../lib/services/animation/attachment_placement";
import Bone from "../bone/bone";

export default class Puppy extends SceneObject {
  builder: CharacterBuilder;
  runtime: CharacterRuntime | null = null;
  shadow: BlobShadow;
  navigator: PuppyNavigator;
  brain: PuppyBrain;
  //runner: PuppySequenceDebugRunner | null = null;

  // GPU animation infrastructure.
  builder_gpu: CharacterBuilderGpu | null = null;
  library_pool: AnimationLibraryPool | null = null;
  animation_pool: AnimationPool | null = null;
  animation_compute_pipeline: AnimationComputePipeline | null = null;
  animation_controller_gpu: AnimationControllerGpu | null = null;
  private attachment_placement: AttachmentPlacement | null = null;
  private bone_pl:Bone | null = null;

  constructor(key: string) {
    super(key);

    this.builder = new CharacterBuilder();

    this.navigator = new PuppyNavigator(this);
    this.navigator.attach(this);

    this.brain = new PuppyBrain(this, this.navigator);
     this.brain.attach(this, this.navigator);

    this.shadow = new BlobShadow("puppy_shadow", 2);
    this.add_child(this.shadow);

   
  }

  async build(
    device: GPUDevice,
    format: GPUTextureFormat,
    shader_service: ShaderService,
    resource_service: ResourceService
  ): Promise<void> {
    this.runtime = await this.builder.build(
      {
        key: this.key,
        shader_name: "papercraft_skinned",
        mesh_name: "puppy",
        texture_name: "puppy",
        animation_name: "puppy_rm",
        default_loop_index: PuppyClips.Idle_1,
      },
      device,
      format,
      shader_service,
      resource_service
    );

    await this.build_gpu_animation(device, shader_service);

    await super.build(device, format, shader_service, resource_service);

    this.transform.set_scale(1, 1, 1);
    this.transform.update_matrix();

    this.brain.initialize_spawn(0.5132098197937012, 0, 6.3);
    
    this.register_collisions(Stage.instance().collision_service);

    this.shadow.update_shadow(
      this.transform.position[0],
      this.transform.position[1],
      this.transform.position[2]
    );

  }

/*
// 30 degrees around X axis
const angle = 30 * Math.PI / 180;
const halfAngle = angle / 2;
const mouth_offset = makeAttachmentOffset(
  0, -0.02, -0.15,
  Math.sin(halfAngle), 0, 0, Math.cos(halfAngle)

  // No rotation
0, 0, 0, 1

// 90° around Y
0, Math.SQRT1_2, 0, Math.SQRT1_2

// 45° around X
Math.sin(Math.PI/8), 0, 0, Math.cos(Math.PI/8)

// Arbitrary axis-angle
const half = angle / 2;
const s = Math.sin(half);
// quat = (axis.x * s, axis.y * s, axis.z * s, cos(half))
);*/

  makeAttachmentOffset(
  x: number, y: number, z: number,
  rx: number, ry: number, rz: number, rw: number
): Float32Array {
  return trsToMat4(
    new Float32Array([x, y, z]),
    new Float32Array([rx, ry, rz, rw]),
    new Float32Array([1, 1, 1])
  );
}

  private async build_gpu_animation(
    device: GPUDevice,
    shader_service: ShaderService
  ): Promise<void> {
    if (!this.runtime) return;

    const model = this.runtime.model;

    if (!model.meshes.length) {
      throw new Error("Puppy model contains no mesh primitives");
    }

    const primitive = model.meshes[0];
    const skin_index = primitive.skinIndex;
    if (skin_index === undefined) {
      throw new Error("Puppy primitive is not associated with a skin");
    }

    const mesh_node_index = primitive.nodeIndex;

    // Log joint names and indices
  /*const skin = model.skins[skin_index];
  for (let i = 0; i < skin.joints.length; i++) {
    const nodeIndex = skin.joints[i];
    const nodeName = model.nodes[nodeIndex].name ?? `node_${nodeIndex}`;
    console.log(`joint[${i}] node[${nodeIndex}] "${nodeName}"`);
  }*/

    this.builder_gpu = new CharacterBuilderGpu(this.key);
    await this.builder_gpu.build(
      device,
      model,
      this.runtime.animation_library,
      skin_index,
      mesh_node_index
    );

    this.library_pool = new AnimationLibraryPool();
    this.library_pool.initialize();
    this.library_pool.populate(this.builder_gpu);

    this.animation_pool = new AnimationPool();
    this.animation_pool.initialize(
      device,
      this.key,
      this.builder_gpu.node_count,
      this.builder_gpu.joint_count
    );

    this.animation_compute_pipeline = new AnimationComputePipeline(this.key);
    await this.animation_compute_pipeline.build(device, shader_service);

    this.animation_controller_gpu = new AnimationControllerGpu(
      this.key,
      this.runtime.animation_library,
      this.builder_gpu,
      this.library_pool,
      this.animation_pool,
      this.animation_compute_pipeline
    );

    //attachments 
    const mouth_offset_matrix = this.makeAttachmentOffset(-0.0066, 0.1033, 0.0115, 0, 0, 0, 1); 
    const mouth = new JointAttachment("mouth",8 , mouth_offset_matrix);
    mouth.bind(model, skin_index, mesh_node_index);
    this.add_attachment(mouth);

    const frisbee_in_mouth_offset_matrix = this.makeAttachmentOffset(-0.0066, 0.1033, 0.0115, 0, 0, 0, 1); 
    const frisbee_in_mouth = new JointAttachment("frisbee_in_mouth",8 , mouth_offset_matrix);
    frisbee_in_mouth.bind(model, skin_index, mesh_node_index);
    this.add_attachment(frisbee_in_mouth);


    /*this.attachment_placement = new AttachmentPlacement(mouth);
    this.bone_pl = new Bone("pl_bone",0);
    this.bone_pl.transform.set_scale(0.5, 0.5, 0.5);
    this.bone_pl.transform.update_matrix();
    this.add_child(this.bone_pl);
    this.attach_child(this.bone_pl,"mouth");
    */
   const head_offset_matrix = this.makeAttachmentOffset(0, 0, 0, 0, 0, 0, 1); 
    const head = new JointAttachment("head", 7,head_offset_matrix);
    head.bind(model, skin_index, mesh_node_index);
    this.add_attachment(head);

  
    const hsphere = new SphereCollisionVolume(new Float32Array([0,0,0]), 0.06, "puppy", false,"head");
    hsphere.attachment = head;
    this.add_collision_volume(hsphere);

    // Start with the idle clip so the compute pass has a clip to sample
    // before the navigator or brain takes over.
    this.animation_controller_gpu.playLoopByIndex(PuppyClips.Idle_1);
    //this.runner = new PuppySequenceDebugRunner(this.animation_controller_gpu);
  }

  run_debug_sequence() {
   /* if (this.runner) {
      this.runner.start();
    }*/
  }

  tick(delta_time: number): void {
    if (!this.runtime) return;

    this.brain.tick(delta_time);

    if (this.animation_controller_gpu) {
      this.animation_controller_gpu.tick(delta_time);
    }

    // Update attachments so the sphere knows where the head is
     this.transform.update_matrix();
     this.update_attachments();

    //this.volume_placement.tick(delta_time,0);

    if (this.attachment_placement) 
    {
        this.attachment_placement.tick(delta_time);
    }
   
     super.tick(delta_time);
  }

  override compute(device: GPUDevice, pass: GPUComputePassEncoder, encoder:GPUCommandEncoder): void {
    if (this.animation_controller_gpu) {
      this.animation_controller_gpu.encode_compute(device, pass);
    }

    super.compute(device, pass,encoder);
  }

  override prepare(device: GPUDevice, camera: Camera): void {
    if (!this.runtime) return;

    if (this.animation_controller_gpu) {
      this.runtime.skinned_renderpipeline.prepare(
        device,
        camera,
        this.transform,
        this.animation_controller_gpu
      );
    }

    super.prepare(device, camera);
  }

  override draw(pass: GPURenderPassEncoder): void {
    this.runtime?.skinned_renderpipeline.draw(pass);
    super.draw(pass);
  }

  override readback_encode(device: GPUDevice, encoder: GPUCommandEncoder): void {
    if (this.animation_controller_gpu) {
      this.animation_controller_gpu.readback_encode(device, encoder);
    }

    super.readback_encode(device, encoder);
  }

  override finish_draw(): void {
    this.runtime?.skinned_renderpipeline.finish_draw();

    super.finish_draw();
  }

  override readback_poll(device: GPUDevice): void {
    if (this.animation_controller_gpu) {
      this.animation_controller_gpu.readback_poll();
    }

    super.readback_poll(device);
  }

  ui_message(cmd: string, msg: string): void {
    super.ui_message(cmd, msg);
  }
}